import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const lutealSupport: ContentBlock<"procedure"> = {
  id: makeId("proc-luteal-support"),
  kind: "procedure",
  tags: ["ivf", "fet", "her", "during-cycle", "post-cycle"],
  title: "Luteal Phase Support",
  subtitle:
    "Progesterone supplementation after retrieval or transfer to support implantation",
  body: {
    procedureName: "Luteal Phase Support",
    category: "recovery",
    description:
      "Progesterone supplementation after retrieval or transfer to support potential implantation and early pregnancy.",
    howItWorks:
      "After egg retrieval or embryo transfer, progesterone is administered to maintain the uterine lining and support embryo implantation. This replaces or supplements the progesterone your body would normally produce after ovulation. Routes include vaginal suppositories/gel, intramuscular injections, or oral capsules. Support continues until pregnancy test; if positive, it typically continues through weeks 8-10 of pregnancy.",
    duration: "2 weeks minimum, up to 10 weeks if pregnant",
    whatToExpect: [
      "Daily progesterone administration (vaginal, IM injection, or oral)",
      "Possible side effects: bloating, fatigue, breast tenderness, mood changes",
      "Symptoms can mimic early pregnancy, don't read into them",
      "Continue until your clinic says to stop",
    ],
    forWhom: "her",
    frBody: {
      procedureName: "Soutien de phase lutéale",
      description:
        "Supplémentation en progestérone après la ponction ou le transfert pour soutenir l'implantation et le début de grossesse.",
      howItWorks:
        "Après la ponction ovocytaire ou le transfert d'embryon, la progestérone est administrée pour maintenir la muqueuse utérine et soutenir l'implantation. Elle remplace ou complète la progestérone que votre corps produirait normalement après l'ovulation. Les voies d'administration incluent les ovules ou gel vaginal, l'injection intramusculaire ou les capsules orales. Le soutien se poursuit jusqu'au test de grossesse ; s'il est positif, il est généralement maintenu jusqu'à 8 à 10 semaines de grossesse.",
      duration: "2 semaines minimum, jusqu'à 10 semaines en cas de grossesse",
      whatToExpect: [
        "Administration quotidienne de progestérone (vaginale, IM ou orale)",
        "Effets indésirables possibles : ballonnements, fatigue, sensibilité des seins, changements d'humeur",
        "Les symptômes peuvent ressembler à un début de grossesse, ne pas surinterpréter",
        "À poursuivre jusqu'à ce que votre centre indique d'arrêter",
      ],
    },
  },
  sources: [
    {
      title: "Luteal phase support for assisted reproduction cycles",
      authors:
        "van der Linden M, Buckingham K, Farquhar C, Kremer JAM, Metwally M",
      journal: "Cochrane Database of Systematic Reviews",
      year: 2015,
      url: "https://pubmed.ncbi.nlm.nih.gov/26148507/",
      evidenceLevel: "meta-analysis",
    },
    {
      title:
        "Diagnosis and treatment of luteal phase deficiency: a committee opinion",
      authors: "Practice Committee of the ASRM",
      journal: "Fertility and Sterility",
      year: 2021,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/diagnosis-and-treatment-of-luteal-phase-deciency-a-committee-opinion-2021/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(lutealSupport);
